import { useState } from 'react';
import { X, Search } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  extraLargeTextSize,
  semiBoldText,
  smallTextSize,
  secondaryText,
  primaryText,
  mediumText,
  borderPrimary,
  whiteBG,
} from '../../../../styling/stylingHelper';
import { useFuzzySearch } from '../../../../hooks/useFuzzySearch';
import { CostPriceDuplicateMenuItemApiError } from '../../../../services/costPrice/costPriceService';

interface EditRevenueLinkModalProps {
  currentMenuItemName: string | null;
  revenueItems: string[];
  onSave: (menuItemName: string | null, opts?: { replaceExistingLink: boolean }) => Promise<void>;
  onClose: () => void;
}

const EditRevenueLinkModal = ({
  currentMenuItemName,
  revenueItems,
  onSave,
  onClose,
}: EditRevenueLinkModalProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState<string | null>(currentMenuItemName);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflictingNames, setConflictingNames] = useState<string[] | null>(null);

  // Convert revenueItems array to objects for fuzzy search
  const revenueItemObjects = revenueItems.map(item => ({ name: item }));

  // Filter revenue items by fuzzy search
  const filteredRevenueItems = useFuzzySearch({
    data: revenueItemObjects,
    keys: ['name'],
    query: search,
    threshold: 0.5,
  });

  const handleSelectItem = (itemName: string) => {
    if (isLoading) return;
    setSelectedItem(itemName);
    setConflictingNames(null);
    setError(null);
  };

  const handleRemoveLink = () => {
    if (isLoading) return;
    setSelectedItem(null);
    setConflictingNames(null);
    setError(null);
  };

  const handleSave = async (replaceExistingLink = false) => {
    setIsLoading(true);
    setError(null);
    try {
      await onSave(selectedItem, replaceExistingLink ? { replaceExistingLink: true } : undefined);
      onClose();
    } catch (err) {
      console.error('Failed to save revenue link:', err);
      if (err instanceof CostPriceDuplicateMenuItemApiError) {
        setConflictingNames(err.existingCostPriceNames);
      } else {
        setError(t('costPrice.revenueLink.saveFailed'));
      }
      setIsLoading(false);
    }
  };

  const hasChanged = selectedItem !== currentMenuItemName;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/60 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-card dark:bg-card-dark rounded-none sm:rounded-lg shadow-xl max-w-2xl w-full h-full sm:h-auto max-h-none sm:max-h-[80vh] border border-zinc-200 dark:border-white/20 flex flex-col">
        {/* Header - Fixed */}
        <div className="p-6 border-b border-border dark:border-border-dark flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className={cn(extraLargeTextSize, semiBoldText, primaryText)}>
              {t('costPrice.detail.editRevenueLink')}
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-muted dark:text-muted-dark hover:text-text dark:hover:text-text-dark rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className={cn(smallTextSize, secondaryText)}>
            Link this cost price item to a revenue item to automatically calculate margins
          </p>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted dark:text-muted-dark" />
              <input
                type="text"
                placeholder="Search revenue items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border dark:border-border-dark rounded-lg bg-background dark:bg-background-dark text-text dark:text-text-dark placeholder-muted dark:placeholder-muted-dark focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-dark"
              />
            </div>
          </div>

          {/* Selected Item Display */}
          {selectedItem && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                    Selected Revenue Item
                  </h4>
                  <div className="text-text dark:text-text-dark font-medium">
                    {selectedItem}
                  </div>
                </div>
                <button
                  onClick={handleRemoveLink}
                  className={cn(
                    smallTextSize,
                    mediumText,
                    "px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  )}
                >
                  Remove Link
                </button>
              </div>
            </div>
          )}

          {/* No Link Option */}
          <div
            onClick={handleRemoveLink}
            className={`mb-3 p-3 border rounded-lg cursor-pointer transition-colors ${
              selectedItem === null
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                : 'border-border dark:border-border-dark hover:bg-muted/5 dark:hover:bg-muted-dark/5'
            }`}
          >
            <div className="font-medium text-text dark:text-text-dark">No link</div>
            <div className="text-sm text-muted dark:text-muted-dark">
              Don't link this cost price item to any revenue item
            </div>
          </div>

          {/* Revenue Items List */}
          <div className="space-y-2">
            {filteredRevenueItems.length === 0 ? (
              <div className="p-4 text-center text-muted dark:text-muted-dark">
                {search ? 'No revenue items match your search' : 'No revenue items available'}
              </div>
            ) : (
              filteredRevenueItems.map((item) => {
                const isSelected = selectedItem === item.name;

                return (
                  <div
                    key={item.name}
                    onClick={() => handleSelectItem(item.name)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                        : 'border-border dark:border-border-dark hover:bg-muted/5 dark:hover:bg-muted-dark/5'
                    }`}
                  >
                    <div className="font-medium text-text dark:text-text-dark">
                      {item.name}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 border-t border-borderPrimary dark:border-borderPrimary-dark">
          <div className="p-6">
            {conflictingNames && (
              <p className={cn(smallTextSize, primaryText, 'mb-3 max-h-[30vh] overflow-y-auto break-words')} role="alert">
                <Trans
                  i18nKey="costPrice.revenueLink.moveConfirmBody"
                  count={conflictingNames.length}
                  values={{ item: selectedItem, names: conflictingNames.join(', ') }}
                  components={{ strong: <strong /> }}
                />
              </p>
            )}
            {error && (
              <p className={cn(smallTextSize, mediumText, "mb-3 text-red-600 dark:text-red-400")}>
                {error}
              </p>
            )}
            <div className={conflictingNames ? 'flex flex-col sm:flex-row justify-end gap-3' : 'flex justify-end gap-3'}>
              <button
                onClick={() => {
                  if (conflictingNames) {
                    setConflictingNames(null);
                    setError(null);
                  } else {
                    onClose();
                  }
                }}
                disabled={isLoading}
                className={cn(
                  smallTextSize,
                  secondaryText,
                  semiBoldText,
                  "rounded-xl px-4 py-3",
                  whiteBG,
                  "border",
                  borderPrimary,
                  "hover:bg-gray-50 dark:hover:bg-white/5",
                  "disabled:opacity-50"
                )}
              >
                {t('costPrice.create.cancel')}
              </button>
              <button
                onClick={() => handleSave(conflictingNames !== null)}
                disabled={!hasChanged || isLoading}
                className={cn(
                  smallTextSize,
                  "text-white dark:text-white",
                  semiBoldText,
                  "bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl px-4 py-3",
                  "hover:bg-darkGreenColor/95",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {conflictingNames
                  ? t('costPrice.revenueLink.moveConfirmAction')
                  : isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRevenueLinkModal;
